import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { useChannelList } from '@/api/channel';
import type { OptionType } from '@/api/common';
import { ComboboxPickerMultiple } from '@/components/Form/pickers';
import type { ComboboxProps } from '@/components/Form/pickers/primitives/combobox';

type Props = Omit<ComboboxProps<OptionType, true>, 'items' | 'filteredItems'> & {
  disabled?: boolean;
  className?: string;
  value?: OptionType[];
  onChange?: (value: OptionType[]) => void;
};

const ChannelMultiSelect = ({
  value,
  isLoading: isLoadingProp,
  size,
  disabled,
  className,
  onChange,
  ...rest
}: Props) => {
  const { channels, isLoading } = useChannelList();
  const channelsById = useMemo(
    () => new Map((channels ?? []).map((channel) => [channel.id, channel])),
    [channels],
  );
  const resolvedValue = useMemo(() => {
    if (!value?.length) return [];
    return value
      .map((channel) => channelsById.get(channel.id) ?? channel)
      .filter((channel) => Boolean(channel.name));
  }, [channelsById, value]);

  return (
    <ComboboxPickerMultiple<OptionType>
      data-testid="channel-multiselect"
      items={channels}
      isLoading={isLoadingProp || isLoading}
      disabled={disabled}
      size={size}
      value={resolvedValue}
      onChange={onChange}
      className={className}
      placeholder={t`Select Channels...`}
      loadingPlaceholder={t`Loading Channels...`}
      notFoundContent={<Trans>No channels were found...</Trans>}
      {...rest}
    />
  );
};

export default ChannelMultiSelect;
